import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export async function analyzeFinancialData(financialData, analysisType = 'comprehensive') {
  try {
    const systemPrompts = {
      comprehensive: 'You are an expert financial analyst specializing in accounting data analysis. Analyze the provided financial data comprehensively, identifying trends, patterns, anomalies, and actionable insights. Focus on cash flow health, revenue trends, expense patterns, profitability metrics, and potential risks or opportunities.',
      trends: 'You are a financial trend analyst. Focus on identifying and explaining key trends in the financial data, including revenue growth patterns, expense trends, seasonal variations, and emerging patterns that require attention.',
      anomalies: 'You are a financial anomaly detection specialist. Identify unusual patterns, outliers, unexpected changes, or potential errors in the financial data. Explain why each anomaly is significant and what actions should be taken.',
      insights: 'You are a strategic financial advisor. Generate actionable business insights from the financial data, including recommendations for cost optimization, revenue enhancement, cash flow improvement, and strategic financial decisions.',
    };

    const dataContext = `
Financial Data Summary:
- Cash Flow: ${financialData?.cashFlow || 'N/A'}
- Total Revenue: ${financialData?.revenue || 'N/A'}
- Total Expenses: ${financialData?.expenses || 'N/A'}
- Net Profit: ${financialData?.netProfit || 'N/A'}
- Period: ${financialData?.period || 'Current month'}

Detailed Transactions: ${JSON.stringify(financialData?.transactions || [], null, 2)}

Expense Breakdown: ${JSON.stringify(financialData?.expenseBreakdown || [], null, 2)}

Revenue Sources: ${JSON.stringify(financialData?.revenueSources || [], null, 2)}
`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: systemPrompts?.[analysisType] || systemPrompts?.comprehensive
        },
        {
          role: 'user',
          content: `Please analyze this financial data and provide detailed insights:\n${dataContext}`,
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'financial_analysis_response',
          schema: {
            type: 'object',
            properties: {
              summary: { type: 'string', description: 'Executive summary of the financial analysis' },
              keyFindings: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'List of key findings from the analysis'
              },
              trends: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    category: { type: 'string' },
                    description: { type: 'string' },
                    impact: { type: 'string', enum: ['positive', 'negative', 'neutral'] },
                    confidence: { type: 'string', enum: ['high', 'medium', 'low'] }
                  },
                  required: ['category', 'description', 'impact']
                },
                description: 'Identified trends in the financial data'
              },
              anomalies: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    type: { type: 'string' },
                    description: { type: 'string' },
                    severity: { type: 'string', enum: ['high', 'medium', 'low'] },
                    recommendation: { type: 'string' }
                  },
                  required: ['type', 'description', 'severity']
                },
                description: 'Detected anomalies or unusual patterns'
              },
              recommendations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    title: { type: 'string' },
                    description: { type: 'string' },
                    priority: { type: 'string', enum: ['high', 'medium', 'low'] },
                    expectedImpact: { type: 'string' }
                  },
                  required: ['title', 'description', 'priority']
                },
                description: 'Actionable recommendations based on the analysis'
              },
              riskAssessment: {
                type: 'object',
                properties: {
                  overallRisk: { type: 'string', enum: ['high', 'medium', 'low'] },
                  riskFactors: { type: 'array', items: { type: 'string' } },
                  mitigationStrategies: { type: 'array', items: { type: 'string' } }
                },
                required: ['overallRisk', 'riskFactors']
              },
              confidence: { 
                type: 'number', 
                minimum: 0, 
                maximum: 1,
                description: 'Overall confidence in the analysis (0-1)'
              }
            },
            required: ['summary', 'keyFindings', 'trends', 'anomalies', 'recommendations', 'confidence'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in financial analysis:', error);
    }
    throw new Error(errorInfo.message);
  }
}

export async function generateFinancialInsight(query, context) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are a helpful financial assistant. Provide clear, concise answers to financial questions based on the provided accounting data context.'
        },
        {
          role: 'user',
          content: `Context: ${JSON.stringify(context)}\n\nQuestion: ${query}`,
        },
      ],
      max_tokens: 500,
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating financial insight:', error);
    }
    throw new Error(errorInfo.message);
  }
}

export default { analyzeFinancialData, generateFinancialInsight };
